import { RouteObject } from 'react-router-dom';
import React, { Suspense } from 'react';
import { IMenu, IMenuDTO, MenuType } from 'src/components/types';
import AuthenticationLayout from 'src/layouts/authentication-layout/AuthenticationLayout';
import { NotFound } from 'src/pages/common/NotFound';
import { IMenuService } from 'src/providers/system/types';
import { log } from 'src/services/LogService';
import { isDefined } from 'src/utils/TypeCheckers';
import { BESPOKE_PAGES_MAP, PRE_AUTH_PAGES_MAP, VISIONX_CONSTANTS } from './ComponentMap';
import { extractSafeReturnUrl } from 'src/app/custom-hooks/useAuthGuard.ts';
import FullScreenLoader from 'src/components/common/loading-spinner/FullScreenLoader';

// Lazy-load authenticated-only components to keep them out of the main bundle.
// These are only rendered after login, so they don't need to be in the initial JS.
const VisionXLayout = React.lazy(() => import('src/layouts/visionx-layout/VisionXLayout'));
const MaintainTable = React.lazy(
  () => import('src/components/common/maintain-table/MaintainTable'),
);

class MenuService implements IMenuService {
  private className = 'MenuService-' + new Date().getTime();

  public createPreAuthenticatedRoutes = (): RouteObject[] => {
    const authRoutes = this.getPreAuthenticatedRoutes();
    return [authRoutes];
  };

  public createPostAuthenticatedRoutes = (menuGroups: IMenuDTO[]): RouteObject[] => {
    const backofficePrefix = '/admin';
    const backofficeRoutes: RouteObject = {
      path: backofficePrefix + '/*',
      element: (
        <Suspense fallback={<FullScreenLoader />}>
          <VisionXLayout />
        </Suspense>
      ),
      children: [],
    };
    const visionXRoutes: RouteObject = {
      path: '/',
      element: (
        <Suspense fallback={<FullScreenLoader />}>
          <VisionXLayout />
        </Suspense>
      ),
      children: [],
    };
    const notFoundRoute: RouteObject = {
      path: '*',
      element: <NotFound />,
    };

    menuGroups?.forEach((menuGroup) => {
      menuGroup?.menuItems?.forEach((item) => {
        if (item.menuType === MenuType.ENTITY_GROUP || item.menuType === MenuType.SINGLE_PAGE) {
          switch (item.menuType) {
            case MenuType.ENTITY_GROUP:
              backofficeRoutes.children?.push({
                path: backofficePrefix + '/*/' + item.code,
                element: (
                  <Suspense fallback={<FullScreenLoader />}>
                    <MaintainTable />
                  </Suspense>
                ),
              });
              break;
          }
        } else if ('/' === item.code) {
          /* empty */
        } else {
          log.info(this.className, 'MSG500', 'what should I do with you?', item);
        }
      });
    });

    const routes: RouteObject[] = [this.getPreAuthenticatedRoutes()];
    routes.push(notFoundRoute);

    if (backofficeRoutes?.children?.length) {
      routes.push(backofficeRoutes);
    }

    Object.keys(BESPOKE_PAGES_MAP).forEach((key: string) => {
      BESPOKE_PAGES_MAP[key]?.forEach((route) => visionXRoutes?.children?.push(route));
    });

    if (visionXRoutes?.children?.length) {
      routes.push(visionXRoutes);
    }
    return routes;
  };

  public redirectTo = (
    currentPath: string,
    validSession: boolean,
    hasSession: boolean,
    systemLoaded: boolean,
    homePage?: string,
  ): string | undefined => {
    if (
      !hasSession &&
      (currentPath.startsWith(VISIONX_CONSTANTS.VERIFY_PATH) ||
        currentPath.startsWith(VISIONX_CONSTANTS.TOTP_PATH) ||
        currentPath.startsWith(VISIONX_CONSTANTS.GEO_RESTRICTED_PATH) ||
        currentPath.startsWith(VISIONX_CONSTANTS.FORGOT_PASSWORD_PATH))
    ) {
      return currentPath;
    } else if (validSession && systemLoaded) {
      if (
        isDefined(currentPath) &&
        currentPath !== VISIONX_CONSTANTS.BASE_PATH &&
        !currentPath.startsWith(VISIONX_CONSTANTS.VERIFY_PATH) &&
        !currentPath.startsWith(VISIONX_CONSTANTS.LOGIN_PATH)
      ) {
        return currentPath;
      } else if (isDefined(currentPath)) {
        const { safe, returnUrl } = extractSafeReturnUrl(currentPath, { maxDepth: 10 });
        if (safe) {
          return returnUrl;
        }
      }
      return VISIONX_CONSTANTS.BASE_PATH + homePage;
    } else if (!hasSession && currentPath === VISIONX_CONSTANTS.LOGIN) {
      return VISIONX_CONSTANTS.LOGIN_PATH;
    } else if (!systemLoaded && hasSession) {
      if (currentPath === '/login') {
        return VISIONX_CONSTANTS.BASE_PATH + homePage;
      }
      return currentPath;
    }
    return undefined;
  };

  public getMenuItemsForGroup = (groupName: string, menuGroups: IMenuDTO[]): IMenu[] => {
    return menuGroups
      .filter((menuGroup) => menuGroup.label === groupName)
      .map(
        (menuGroup) =>
          menuGroup?.menuItems?.map((item) => {
            return {
              code: item.code,
              label: item.label,
              icon: item.icon || 'fas fa-user-friends',
              route: '',
            } as IMenu;
          }) || [],
      )
      .flatMap((x: IMenu[]) => x);
  };

  private getPreAuthenticatedRoutes = (): RouteObject => {
    const preAuthRoutes: RouteObject = {
      path: '/',
      element: <AuthenticationLayout />,
      children: [],
    };

    Object.keys(PRE_AUTH_PAGES_MAP).forEach((key: string) => {
      PRE_AUTH_PAGES_MAP[key]?.forEach((route) => preAuthRoutes?.children?.push(route));
    });

    return preAuthRoutes;
  };
}

export default MenuService;
